/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';

import FormControl from '@material-ui/core/FormControl';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';
import WrapIfAdditional from './WrapIfAdditional';
import AssistiveText from './AssistiveText';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';

const useWidgetStyles = makeStyles(theme => ({
  root: {
    paddingTop: '1.5rem',
    maxWidth: '100%',
    [theme.breakpoints.only('sm')]: {
      minWidth: '95%',
    },
  },
  assistiveText: {
    minHeight: '2.2rem',
  },
  emptySpace: {
    minHeight: '0.5rem',
  },
}));

const useExtraStyles = makeStyles(theme => ({
  root: ({ hasErrors, hasWarnings }) => ({
    '& > div > div': {
      [theme.breakpoints.only('sm')]: {
        minWidth: '100%',
      },
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': () => ({
      borderColor: `${(hasErrors && theme.palette.error.main) || (hasWarnings && theme.palette.warning.main)}`,
      border: `2px solid ${(hasErrors && theme.palette.error.main) || (hasWarnings && theme.palette.warning.main)}`,
    }),
    '& .MuiFormLabel-root.Mui-error': {
      color: theme.palette.mainText,
    },
  }),
}));

const useJsonStyles = makeStyles({
  root: data => data,
});

const FieldTemplate = ({
  id,
  children,
  classNames,
  disabled,
  displayLabel,
  label,
  onDropPropertyClick,
  onKeyChange,
  readonly,
  required,
  rawErrors = [],
  rawHelp,
  rawDescription,
  schema,
  uiSchema = {},
}) => {
  const hasErrors = rawErrors?.length > 0 || uiSchema?.[uiSchemaProperties.UI_HELP_SEVERITY] === 'error';
  const hasWarnings = rawHelp?.length > 0 && uiSchema?.[uiSchemaProperties.UI_HELP_SEVERITY] === 'info';

  const classesWidget = useWidgetStyles();
  const classesOther = useExtraStyles({ hasErrors, hasWarnings });
  const classesJson = useJsonStyles({ ...uiSchema?.['ui:options']?.style });

  const isWidgetNode = () => !['object', 'array'].includes(schema?.type);
  const hidden = uiSchema?.['ui:hidden'];

  return hidden ? (
    <></>
  ) : (
    <WrapIfAdditional
      disabled={disabled}
      id={id}
      label={label}
      onDropPropertyClick={onDropPropertyClick}
      onKeyChange={onKeyChange}
      readonly={readonly}
      required={required}
      schema={schema}
      uiSchema={uiSchema}
    >
      <FormControl
        fullWidth={true}
        error={!!rawErrors.length}
        required={required}
        className={clsx({
          [classesJson.root]: !!uiSchema?.['ui:options']?.style,
          [classesWidget.root]: isWidgetNode(),
          [classesOther.root]: true,
          [classNames]: true,
        })}
      >
        {children}
        {displayLabel && rawDescription ? (
          <Typography variant="caption" color="textSecondary">
            {rawDescription}
          </Typography>
        ) : null}

        {rawErrors?.length || rawHelp ? (
          <div className={clsx({ [classesWidget.assistiveText]: isWidgetNode() })}>
            <List dense={true} disablePadding={true}>
              {rawErrors.map((error, i) => {
                return (
                  <ListItem key={i} disableGutters={true}>
                    <AssistiveText id={id} severity="error">
                      {error}
                    </AssistiveText>
                  </ListItem>
                );
              })}
              {rawHelp && (
                <ListItem key="help" disableGutters={true}>
                  <AssistiveText id={id} severity={uiSchema[uiSchemaProperties.UI_HELP_SEVERITY] || 'info'}>
                    {rawHelp}
                  </AssistiveText>
                </ListItem>
              )}
            </List>
          </div>
        ) : (
          <div className={classesWidget.emptySpace} />
        )}
      </FormControl>
    </WrapIfAdditional>
  );
};

export default FieldTemplate;
